import { camelCase } from 'lodash';

import {
  IImportColumnAliasesData,
  IImportCreatePartManufacturersData,
  IImportCreatePartsData,
  IImportNotValidPartsPayload,
  IImportPart,
  IImportPartsData,
  IImportPartsPayload,
  IImportPartsStatus,
  IImportPayload,
  IImportPreviewRowsPayload,
  IImportUpsertPartsData,
  ISaveUomsData,
  IUomListPayload,
  IUomListRow,
  IValidateManufacturers,
  IValidateParts,
  IValidateUoms,
} from 'bom/types';
import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';

export const getImportPreviewRows = async (
  requestPayload: IImportPreviewRowsPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<string[]> & { headerRow: string[] }> => {
  const { data } = await partfinitiClient.get(
    `/import/preview/rows?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return {
    ...mapRecordsFromServer<string[]>(data?.data, data?.recordsFiltered),
    headerRow: objectKeysSnakeCaseToCamelCase(data?.headerRow || {}),
  };
};

export const createColumnAliases = async (data: IImportColumnAliasesData) =>
  partfinitiClient.postForm('/import/column-name/save-column-aliases', data, {
    skipResultMapping: true,
  });

export const createImportParts = async (data: IImportCreatePartsData) =>
  partfinitiClient.postForm('/import/preview/save-parts', data, { skipResultMapping: true });

export const createImportPartsStatus = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<IImportPartsStatus> => {
  const { data } = await partfinitiClient.get(
    `/import/preview/save-parts-status?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const importValidateManufacturers = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<IValidateManufacturers> => {
  const { data } = await partfinitiClient.get(
    `/import/validate/manufacturers?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return { known: data?.result?.known, unknown: data?.result?.unknown, valid: data?.valid };
};

export const createImportPartManufacturers = async (data: IImportCreatePartManufacturersData) =>
  partfinitiClient.postForm('/import/parts/save-manufacturers', data, {
    skipResultMapping: true,
    excludeKeys: ['map', 'omit'],
  });

export const importValidateParts = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<IValidateParts> => {
  const { data } = await partfinitiClient.get(
    `/import/validate/parts?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return { unknownManufacturer: data?.data?.unknown_manufacturer, valid: data?.valid };
};

export const importNotValidParts = async (
  payload: IImportNotValidPartsPayload
): Promise<GetManyResponse<IImportPart>> => {
  const { data } = await partfinitiClient.get(
    `/import/list/parts?${getQueryStringToSnakeCase(payload)}`
  );

  const records = data?.data.map((importPart) => ({
    ...importPart,
    manufacturer_options: JSON.parse(importPart?.manufacturer_options || ''),
  }));

  return mapRecordsFromServer<IImportPart>(records, data?.recordsFiltered);
};

export const upsertImportParts = async (data: IImportUpsertPartsData) =>
  partfinitiClient.postForm('/import/parts/save-bulk', data, { skipResultMapping: true });

export const getImportPartColumns = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<string[]> => {
  const { data } = await partfinitiClient.get(
    `/import/parts/columns?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return (data?.columns as string[])?.map((col: string) => camelCase(col));
};

export const getImportParts = async (payload: IImportPartsPayload, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/import/parts/list?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return mapRecordsFromServer<IImportPart>(data?.data, data?.recordsFiltered);
};

export const importParts = async (data: IImportPartsData) =>
  partfinitiClient.postForm('/import/import/start', data, { skipResultMapping: true });

export const importPartsStatus = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<IImportPartsStatus> => {
  const { data } = await partfinitiClient.get(
    `/import/import/status?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data);
};

export const importValidateUoms = async (
  payload: IImportPayload,
  signal?: AbortSignal
): Promise<IValidateUoms> => {
  const { data } = await partfinitiClient.get(
    `/import/validate/uoms?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return {
    mismatch: data?.result?.mismatch ?? 0,
    unknown: data?.result?.unknown ?? 0,
    valid: data?.valid,
  };
};

export const getUomList = async (payload: IUomListPayload, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/import/parts/uom-list?${getQueryStringToSnakeCase(payload)}`,
    signal
  );

  return mapRecordsFromServer<IUomListRow>(data?.data, data?.recordsFiltered);
};

export const saveUoms = async (data: ISaveUomsData) =>
  partfinitiClient.postForm('/import/parts/save-uoms', data, {
    skipResultMapping: true,
    excludeKeys: ['map', 'omit'],
  });
